import React, {
  useCallback,
  useState,
  useEffect,
  forwardRef,
  RefObject,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogsContext } from '../../contexts/Dialog';
import { useSnackbarContext } from '../../contexts/Snackbar';
import { useEventTrackerContext } from '../../contexts/EventTracker';
import { usePlaidLink } from 'react-plaid-link';
import { pathname, openWiseConnectPage } from '../../utils';
import { DialogNames } from '../../Types';
import { PropTypes } from '@material-ui/core';
import messages from '../../messages';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import ChooseBankOption from './ConnectBankOptions/Dialog';
import SelectBankAccout from './SelectBankAccount/Dialog';

const useStyles = makeStyles((theme: any) => ({
  button: {
    fontSize: ({ fontSize }: any) => `${fontSize}em`,
    minWidth: ({ minWidth }: any) => {
      return minWidth;
    },
  },
}));

interface ConnectBankProps {
  plaidLinkToken: string;
  onSuccess: (...args) => void;
  oAuthFlow?: boolean;
  buttonText?: string;
  buttonVariant?: string;
  buttonFontSize?: number;
  buttonFontColor?: string;
  buttonMinWidth?: number;
  buttonColor?: PropTypes.Color;
  extraClasses?: string;
  buttonRef?: RefObject<any>;
  openImmidiately?: boolean;
  goBack?: (...args) => void;
}

export default function ConnectBank({
  plaidLinkToken,
  onSuccess,
  oAuthFlow = false,
  buttonText = 'Connect Bank Account',
  buttonVariant = 'contained',
  buttonFontSize = 1,
  buttonMinWidth = 0,
  buttonColor = undefined,
  extraClasses = undefined,
  buttonRef = undefined,
  openImmidiately = false,
  goBack = undefined,
}: ConnectBankProps) {
  const dialog: any = useDialogsContext();
  const classes = useStyles({
    fontSize: buttonFontSize,
    minWidth: buttonMinWidth,
  });
  const snackbar = useSnackbarContext();
  const history = useHistory();
  const { trackEvent } = useEventTrackerContext();
  const [plaidBank, setPlaidBank] = useState(null);

  const onPlaidSuccess = useCallback(async (token, bank) => {
    setPlaidBank({ ...bank, publicToken: token });
    dialog.open(DialogNames.SelectBankAccount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPlaidExit = useCallback(async (error, metadata) => {
    trackEvent({ event: 'exited_plaid', status: metadata?.status, error });
    if (oAuthFlow) {
      history.push(pathname.account.main.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const config = {
    token: plaidLinkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
  };
  if (oAuthFlow) {
    config['receivedRedirectUri'] = window.location.href;
  }
  const { open, ready } = usePlaidLink(config);

  function openPlaidDialog(handleClose: () => any) {
    handleClose();
    if (plaidLinkToken === 'error') {
      snackbar.show(messages.bank.errors.misssingLinkToken, 'error');
    } else {
      open();
    }
  }

  async function openWise(
    handleClose: () => any,
    windowRef?: WindowProxy | null,
  ) {
    try {
      await openWiseConnectPage(windowRef);
      handleClose();
    } catch (error) {
      snackbar.show(messages.bank.errors.wiseRedirect, 'error');
    }
  }

  function openChooseBankOption() {
    dialog.open('chooseBankOption');
  }

  useEffect(() => {
    if (!ready || !oAuthFlow) return;
    open();
  }, [ready, open, oAuthFlow]);

  useEffect(() => {
    if (openImmidiately) {
      openChooseBankOption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      {!oAuthFlow && !openImmidiately && (
        <Button
          color={buttonColor || 'primary'}
          variant={buttonVariant === 'contained' ? 'contained' : 'outlined'}
          className={`${classes.button} ${extraClasses || ''}`}
          onClick={openChooseBankOption}
          ref={buttonRef}
        >
          {buttonText}
        </Button>
      )}
      <ChooseBankOption
        openPlaidDialog={openPlaidDialog}
        openWise={openWise}
        goBack={goBack}
      />
      {plaidBank && (
        <SelectBankAccout
          accounts={plaidBank.accounts}
          bank={plaidBank.institution.name}
          publicToken={plaidBank.publicToken}
          onSuccess={onSuccess}
          goBack={goBack}
        />
      )}
    </React.Fragment>
  );
}

export const ReferableConnectButton = forwardRef<any, any>((props, ref) => {
  return <ConnectBank {...props} buttonRef={ref} />;
});
